import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pl", label: "Polski" },
  { code: "ar", label: "العربية" },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Keep the document direction and language attribute in sync so Arabic
  // renders right-to-left and other languages left-to-right.
  useEffect(() => {
    const lang = (i18n.language || "en").split("-")[0];
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm text-white/80 transition-colors hover:text-brand-gold whitespace-nowrap"
        aria-label="Change language"
      >
        <span className="flex h-8 w-8 items-center justify-center">
          <i className="ri-global-line text-xl"></i>
        </span>
        <span className="hidden lg:inline text-xs font-semibold uppercase tracking-wide">
          {current.code.toUpperCase()}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 rounded-lg border border-white/10 bg-brand-dark2 py-1">
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => {
                i18n.changeLanguage(l.code);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-4 py-2.5 text-sm transition-colors whitespace-nowrap ${
                i18n.language === l.code
                  ? "text-brand-gold"
                  : "text-white/80 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span>{l.label}</span>
              {i18n.language === l.code && <i className="ri-check-line"></i>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}